// B2B catalogue constants: categories, statuses, and per-category line-item
// field definitions (single source of truth for the admin editor and the
// public spec summaries).
import { Laptop, Monitor, HardDrive, Plug, MemoryStick, Package, LayoutGrid } from "lucide-react";

export const CATEGORY_ORDER = ["all", "laptops", "desktops", "hdd_ssd", "adapters", "ram", "other"];

export const CATEGORY_LABELS = {
  all: "All",
  laptops: "Laptops",
  desktops: "Desktops",
  hdd_ssd: "HDD/SSD",
  adapters: "Adapters",
  ram: "RAM",
  other: "Other",
};

export const CATEGORY_ICON = {
  all: LayoutGrid,
  laptops: Laptop,
  desktops: Monitor,
  hdd_ssd: HardDrive,
  adapters: Plug,
  ram: MemoryStick,
  other: Package,
};

// Effective statuses returned by the API.
export const STATUS_CONFIG = {
  AVAILABLE: { label: "Available", cls: "text-emerald-700 bg-emerald-50 ring-1 ring-emerald-200" },
  LOW_STOCK: { label: "Low Stock", cls: "text-amber-700 bg-amber-50 ring-1 ring-amber-200" },
  LOT_SOLD: { label: "Lot Sold", cls: "text-rose-700 bg-rose-50 ring-1 ring-rose-200" },
  ON_HOLD: { label: "On Hold", cls: "text-blue-700 bg-blue-50 ring-1 ring-blue-200" },
};

// Manual status options for admin (null/"" = auto-compute).
export const MANUAL_STATUS_OPTIONS = [
  { v: "", l: "Auto (from quantity)" },
  { v: "AVAILABLE", l: "Available" },
  { v: "LOW_STOCK", l: "Low Stock" },
  { v: "LOT_SOLD", l: "Lot Sold" },
  { v: "ON_HOLD", l: "On Hold" },
];

// Per-category line-item fields. `key` maps 1:1 to a b2b_lot_item column.
export const CATEGORY_FIELDS = {
  laptops: [
    { key: "brand", label: "Brand" },
    { key: "model", label: "Model" },
    { key: "processor", label: "Processor" },
    { key: "ram", label: "RAM" },
    { key: "storage", label: "Storage" },
    { key: "display", label: "Display" },
    { key: "condition", label: "Condition" },
  ],
  desktops: [
    { key: "brand", label: "Brand" },
    { key: "model", label: "Model" },
    { key: "processor", label: "Processor" },
    { key: "ram", label: "RAM" },
    { key: "storage", label: "Storage" },
    { key: "graphics", label: "Graphics" },
    { key: "condition", label: "Condition" },
  ],
  hdd_ssd: [
    { key: "brand", label: "Brand" },
    { key: "technology", label: "Technology", placeholder: "SATA / NVMe" },
    { key: "form_factor", label: "Form Factor", placeholder: '2.5" / 3.5" / M.2' },
    { key: "capacity", label: "Capacity", placeholder: "500GB / 1TB / 2TB" },
    { key: "interface", label: "Interface" },
    { key: "health", label: "Health", placeholder: "96%" },
    { key: "power_on_hours", label: "Power-On Hours" },
    { key: "condition", label: "Condition" },
  ],
  adapters: [
    { key: "brand", label: "Brand", placeholder: "HP / Dell / Lenovo / Mix" },
    { key: "adapter_type", label: "Type", placeholder: "Original / Compatible" },
    { key: "pin", label: "Pin" },
    { key: "wattage", label: "Wattage", placeholder: "65W / 90W / 120W" },
    { key: "condition", label: "Condition" },
  ],
  ram: [
    { key: "brand", label: "Brand" },
    { key: "ram_platform", label: "Laptop / Desktop", placeholder: "Laptop (SO-DIMM)" },
    { key: "ddr_generation", label: "DDR Generation", placeholder: "DDR3 / DDR4 / DDR5" },
    { key: "capacity", label: "Capacity", placeholder: "4GB / 8GB / 16GB" },
    { key: "bus_speed", label: "Bus Speed", placeholder: "3200 MHz" },
    { key: "condition", label: "Condition" },
  ],
  other: [
    { key: "product_name", label: "Product Name" },
    { key: "brand", label: "Brand" },
    { key: "specifications", label: "Specifications" },
  ],
};

const TITLE_KEYS = ["product_name", "model", "brand"];

// Build a compact "Brand Model" title for a line item.
export function itemTitle(cat, it) {
  if (cat === "other") return it.product_name || it.brand || "Item";
  const brand = it.brand ? `${it.brand} ` : "";
  const model = it.model || it.product_name || "";
  return (brand + model).trim() || it.brand || "Item";
}

// Build a readable spec summary from the category's non-title fields.
export function itemSpecSummary(cat, it) {
  const fields = CATEGORY_FIELDS[cat] || CATEGORY_FIELDS.other;
  return fields
    .filter((f) => !TITLE_KEYS.includes(f.key))
    .map((f) => it[f.key])
    .filter((v) => v !== null && v !== undefined && String(v).trim() !== "")
    .join(" · ");
}

export const fmtMoney = (v) =>
  v === null || v === undefined ? null : `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
